import React, { useState } from "react";
import Wrapper from "./shared/Wrapper";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, Sparkles, MessageSquare, AlertCircle } from "lucide-react";
import SuccessModal from "../components/SuccessModal";

const BASE_URL = import.meta.env.VITE_BASE_URL || "http://localhost:3001";

export default function Contact() {
  // États du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // États UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Validation côté client
  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = 'Le nom doit contenir au moins 2 caractères';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = 'Veuillez entrer un email valide';
    }

    if (!formData.subject || formData.subject.trim().length < 3) {
      errors.subject = 'Le sujet doit contenir au moins 3 caractères';
    }

    if (!formData.message || formData.message.trim().length < 10) {
      errors.message = 'Le message doit contenir au moins 10 caractères';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Gestion des changements de champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Nettoyer l'erreur de validation pour ce champ
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // Appel API
      const response = await fetch(`${BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Succès
        setSubmitStatus('success');
        setShowSuccessModal(true);

        // Reset du formulaire
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        // Erreur serveur
        throw new Error(data.error || 'Erreur lors de l\'envoi du message');
      }

    } catch (error) {
      console.error('Erreur:', error);
      setSubmitStatus('error');

      if (error.message.includes('Failed to fetch')) {
        setErrorMessage('Impossible de contacter le serveur. Assurez-vous que le serveur backend est démarré.');
      } else {
        setErrorMessage(error.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="bg-black text-white overflow-hidden">
      {/* Background avec étoiles */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-black to-blue-900 z-0" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(6, 182, 212, 0.1),transparent_50%)] z-0" />

      {/* Étoiles animées */}
      <div className="fixed inset-0 overflow-hidden z-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
            initial={{
              x: Math.random() * 100 + 'vw',
              y: Math.random() * 100 + 'vh'
            }}
            animate={{
              x: [null, Math.random() * 100 + 'vw'],
              y: [null, Math.random() * 100 + 'vh']
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      <Wrapper>
        <section className="relative overflow-hidden py-24 min-h-screen">
          {/* Éléments de fond animés */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              animate={{ y: [0, -40, 0], x: [0, 20, 0], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-1/4 left-10 w-16 h-16 bg-blue-500/10 rounded-full blur-xl"
            />
            <motion.div
              animate={{ y: [0, 30, 0], x: [0, -15, 0], opacity: [0.2, 0.5, 0.2] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-1/3 right-20 w-24 h-24 bg-cyan-500/10 rounded-full blur-xl"
            />
            <motion.div
              animate={{ rotate: 360, scale: [1, 1.1, 1] }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              className="absolute -top-24 -right-24 w-48 h-48 border border-blue-500/5 rounded-full"
            />
            <motion.div
              animate={{ rotate: -360, scale: [1.1, 1, 1.1] }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-24 -left-24 w-56 h-46 border border-cyan-500/5 rounded-full"
            />
          </div>

          <div className="max-w-6xl mx-auto px-6 md:px-10 relative z-10">
            {/* En-tête avec animation */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-15"
            >
              {/* <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full mb-6 border border-blue-500/20">
                <Phone className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400 font-medium">Contact Premium</span>
              </div> */}

              <motion.h1
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6"
              >
                <span className="text-white">Contactez</span>
                <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">
                  Nous
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto px-4 sm:px-0"
              >
                Prêt à transformer votre vision en réalité digitale ? Parlons-en !
              </motion.p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start"
            >
              {/* Section coordonnées */}
              <motion.div variants={itemVariants} className="space-y-8">
                <motion.h2
                  whileHover={{ x: 10 }}
                  className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent"
                >
                  Discutons de votre projet
                </motion.h2>

                <motion.p variants={itemVariants} className="text-gray-300 text-base sm:text-lg leading-relaxed">
                  Notre équipe d'experts est prête à vous accompagner dans la réalisation de vos projets digitaux les plus ambitieux.
                </motion.p>

                {/* Cartes de contact */}
                <div className="space-y-6">

                  {[
                    {
                      icon: <Mail className="text-blue-500" size={24} />,
                      text: "info@myriade-groupe.com",
                      subtitle: "Envoyez-nous un email",
                      color: "from-blue-500/10 to-cyan-500/10",
                      hoverColor: "hover:from-blue-500/20 hover:to-cyan-500/20"
                    },
                    {
                      icon: <Phone className="text-blue-500" size={24} />,
                      text: "+228 90 04 88 77 | +228 99 47 10 77",
                      subtitle: "Appelez-nous directement",
                      color: "from-cyan-500/10 to-green-500/10",
                      hoverColor: "hover:from-cyan-500/20 hover:to-green-500/20"
                    },
                    {
                      icon: <MapPin className="text-blue-500" size={24} />,
                      text: "Derrière l'hotel concorde, 08 BP 8672 Lomé, Adidoadin, Lomé – Togo",
                      subtitle: "Venez nous rencontrer",
                      color: "from-blue-500/10 to-green-500/10",
                      hoverColor: "hover:from-blue-500/20 hover:to-green-500/20"
                    },
                    {
                      icon: <Clock className="text-blue-500" size={24} />,
                      text: "Lundi - Vendredi : 8H00 - 17H00",
                      subtitle: "Nos horaires d'ouverture",
                      color: "from-cyan-500/10 to-blue-500/10",
                      hoverColor: "hover:from-cyan-500/20 hover:to-blue-500/20"
                    }

                  ].map((item, index) => (

                    <motion.div
                      key={index}
                      variants={itemVariants}
                      whileHover={{
                        scale: 1.03,
                        x: 10,
                        transition: { type: "spring", stiffness: 300 }
                      }}
                      className={`relative bg-gradient-to-br ${item.color} backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-gray-700 ${item.hoverColor} transition-all duration-300 group`}
                    >

                      {/* Effet de bordure animée */}
                      <motion.div
                        className="absolute inset-0 rounded-2xl border-2 border-transparent"
                        animate={{
                          borderColor: ['rgba(59, 130, 246, 0)', 'rgba(59, 130, 246, 0.3)', 'rgba(59, 130, 246, 0)']
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />

                      <div className="relative z-10 flex items-center gap-4">

                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.3 }}
                          className="bg-gray-800/70 p-3 rounded-xl shadow-lg group-hover:bg-gradient-to-br group-hover:from-blue-500/20 group-hover:to-cyan-500/20 transition-all duration-300"
                        >
                          {item.icon}
                        </motion.div>

                        <div className="flex-1 min-w-0">
                          <p className="text-base sm:text-lg md:text-xl font-semibold text-white break-words">{item.text}</p>
                          <p className="text-sm sm:text-base text-cyan-300">{item.subtitle}</p>
                        </div>

                      </div>

                    </motion.div>

                  ))}

                </div>

              </motion.div>

              {/* Formulaire de contact */}
              <motion.div variants={itemVariants} className="relative">

                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-3 -right-3 w-6 h-6 bg-blue-500 rounded-full"
                />
                <motion.div
                  animate={{ scale: [1.2, 1, 1.2], opacity: [0.6, 0.4, 0.6] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute -bottom-3 -left-3 w-5 h-5 bg-cyan-500 rounded-full"
                />

                <motion.form
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="bg-gray-900/50 backdrop-blur-xl shadow-2xl rounded-3xl p-6 sm:p-8 space-y-4 relative border border-gray-700"
                >
                  {/* En-tête du formulaire */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20">
                      <MessageSquare className="w-6 h-6 text-blue-400" />
                    </div>
                    <motion.h3
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      className="text-2xl sm:text-3xl font-bold text-white"
                    >
                      Envoyez un message
                    </motion.h3>
                  </div>

                  {/* Champs du formulaire */}
                  {[
                    { label: 'Nom complet', name: 'name', type: 'text' },
                    { label: 'Email', name: 'email', type: 'email' },
                    { label: 'Téléphone (optionnel)', name: 'phone', type: 'tel' },
                    { label: 'Sujet', name: 'subject', type: 'text' }

                  ].map((field, index) => (

                    <motion.div
                      key={field.name}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="space-y-2"
                    >
                      <label className="block text-gray-300 font-medium">{field.label}</label>
                      <motion.input
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        whileFocus={{
                          scale: 1.02,
                          boxShadow: "0 0 0 2px rgba(6, 182, 212, 0.5)"
                        }}
                        type={field.type}
                        className={`w-full bg-gray-800/50 border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white transition-all duration-300 ${validationErrors[field.name] ? 'border-red-500' : 'border-gray-700'
                          }`}
                        placeholder={`Votre ${field.label.toLowerCase()}`}
                      />

                      {validationErrors[field.name] && (

                        <motion.p
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-sm flex items-center gap-1"
                        >
                          <AlertCircle className="w-4 h-4" />
                          {validationErrors[field.name]}
                        </motion.p>
                      )}

                    </motion.div>

                  ))}

                  {/* Champ message */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 }}
                    className="space-y-2"
                  >
                    <label className="block text-gray-300 font-medium">Message</label>
                    <motion.textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      whileFocus={{
                        scale: 1.02,
                        boxShadow: "0 0 0 2px rgba(6, 182, 212, 0.5)"
                      }}
                      className={`w-full h-22 bg-gray-800/50 border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white resize-none transition-all duration-300 ${validationErrors.message ? 'border-red-500' : 'border-gray-700'
                        }`}
                      placeholder="Décrivez votre projet en détail..."
                    />
                    {validationErrors.message && (
                      <motion.p
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-400 text-sm flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {validationErrors.message}
                      </motion.p>
                    )}
                  </motion.div>

                  {/* Bouton d'envoi */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{
                      scale: 1.05,
                      boxShadow: "0 10px 30px rgba(6, 182, 212, 0.3)"
                    }}
                    whileTap={{ scale: 0.95 }}
                    animate={isSubmitting ? {
                      scale: [1, 0.98, 1],
                      transition: { repeat: Infinity, duration: 0.6 }
                    } : {}}
                    className="group relative w-full cursor-pointer bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-4 rounded-xl font-semibold shadow-xl overflow-hidden transition-all duration-300 disabled:opacity-70"
                  >
                    {/* Effet de fond animé */}
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <span className="relative z-10 flex items-center justify-center gap-3">
                      {isSubmitting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          <Send size={20} />
                        </motion.div>
                      ) : (
                        <>
                          <Send size={20} className="group-hover:translate-x-1 transition-transform" />
                          <span className="text-sm sm:text-base">Envoyer le message à Myriade</span>
                        </>
                      )}
                    </span>
                  </motion.button>

                  {/* Message d'erreur global */}
                  {submitStatus === 'error' && errorMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3"
                    >
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 font-medium">Erreur d'envoi</p>
                        <p className="text-red-300 text-sm mt-1">{errorMessage}</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Note de confidentialité */}
                  <p className="text-[12px] text-gray-400 text-center pt-4">
                    Vos données sont sécurisées et ne seront jamais partagées avec des tiers.
                  </p>
                </motion.form>

                {/* Modal de succès */}
                <SuccessModal
                  isOpen={showSuccessModal}
                  onClose={() => setShowSuccessModal(false)}
                  userName={formData.name.split(' ')[0] || 'Client'}
                />

              </motion.div>
            </motion.div>

            {/* CTA supplémentaire */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-24 text-center"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full mb-6 border border-blue-500/20">
                <Phone className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400 font-medium">Réponse sous 24h</span>
              </div>

              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Une urgence ? <span className="text-cyan-400">Contactez-nous immédiatement</span>
              </h3>

              <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-8">
                Notre équipe de support est disponible pour répondre à vos besoins urgents en temps réel.
              </p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 border-2 border-blue-500/30 text-white font-semibold rounded-xl hover:border-cyan-500/50 hover:bg-white/5 transition-all duration-300"
                onClick={() => window.location.href = "tel:+22890048877"}
              >
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Appeler maintenant
                </span>
              </motion.button>
            </motion.div>
          </div>
        </section>
      </Wrapper>
    </div>
  );
}
